// The contacts CSV import's "Map columns" step (Import board): field labels, the status line and
// badges of the mapping table, the saved mapping, and the small rules the import dialogs share
// (file checks, the duplicates file). Pure functions only; the dialogs live in components/csv.
import type {
  ContactImportDuplicateMode,
  ContactImportField,
  ContactImportMapping,
  ContactImportMappingEntry,
} from '@/lib/api'
import { apiErrorMessage } from '@/lib/formErrors'

/** The contact fields a column can feed, in the order the "Maps to" select lists them (the API's order). */
export const CONTACT_IMPORT_FIELDS: readonly ContactImportField[] = Object.freeze([
  'firstName',
  'lastName',
  'fullName',
  'email',
  'phone',
  'mobile',
  'whatsapp',
  'jobTitle',
  'companyName',
  'tags',
  'source',
  'description',
  'priority',
] as ContactImportField[])

/** What each field is called in the dialog (the API's own errors say "First name" / "Full name"). */
export const CONTACT_FIELD_LABELS: Record<ContactImportField, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  fullName: 'Full name',
  email: 'Email',
  phone: 'Phone',
  mobile: 'Mobile phone',
  whatsapp: 'WhatsApp',
  jobTitle: 'Job title',
  companyName: 'Company',
  tags: 'Tags',
  source: 'Source',
  description: 'Description',
  priority: 'Priority',
}

/** The fields a row's name can come from: without a column mapped to one of them every row fails. */
export const NAME_FIELDS: readonly ContactImportField[] = ['firstName', 'fullName', 'lastName']

// --- Status line and badges ------------------------------------------------------------------------

export interface MatchSummary {
  /** Columns that are imported: mapped to a field, or kept without one (Remarks, Custom field…). */
  matched: number
  total: number
  /** Columns flagged "Check this". */
  toCheck: number
}

export function matchSummary(mapping: ContactImportMappingEntry[]): MatchSummary {
  return {
    matched: mapping.filter((entry) => entry.target !== null || !!entry.keptAs).length,
    total: mapping.length,
    toCheck: mapping.filter((entry) => entry.confidence === 'check').length,
  }
}

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']

function columns(n: number): string {
  return `${n} column${n === 1 ? '' : 's'}`
}

/** The line above the mapping table: "We matched 6 of 8 columns. Check the two marked below." */
export function matchStatusLine({ matched, total, toCheck }: MatchSummary): string {
  let line: string
  if (total === 0) return 'This file has no columns.'
  if (matched === 0) {
    return total === 1
      ? 'We could not match the file’s only column. Choose a field for it to import it.'
      : `We could not match any of the ${columns(total)}. Choose a field for each column you want to import.`
  }
  if (matched === total) line = total === 1 ? 'We matched the file’s only column.' : `We matched all ${columns(total)}.`
  else line = `We matched ${matched} of ${columns(total)}.`
  if (toCheck > 0) line += ` Check the ${NUMBER_WORDS[toCheck] ?? String(toCheck)} marked below.`
  return line
}

export type MatchTone = 'exact' | 'likely' | 'check' | 'skipped' | 'kept'

/**
 * The Match badge of a row. `chosen`: the user picked this row's field themselves (the API then
 * reports it `exact`), shown as "Set by you". A column without a field that the import still stores
 * is "Kept as <label>", never "Skipped".
 */
export function matchBadge(entry: ContactImportMappingEntry, chosen = false): { label: string; tone: MatchTone } {
  if (entry.target === null) {
    if (entry.keptAs) return { label: `Kept as ${entry.keptAs}`, tone: 'kept' }
    if (entry.confidence === 'check' && !chosen) return { label: 'Check this', tone: 'check' }
    return { label: 'Skipped', tone: 'skipped' }
  }
  if (chosen) return { label: 'Set by you', tone: 'exact' }
  if (entry.confidence === 'check') return { label: 'Check this', tone: 'check' }
  if (entry.confidence === 'likely') return { label: 'Likely', tone: 'likely' }
  return { label: 'Exact', tone: 'exact' }
}

/**
 * The "no field" option of a row's select. Sending null for a kept column keeps it (only a field
 * replaces it), so that option says so.
 */
export function noFieldLabel(entry: ContactImportMappingEntry): string {
  if (entry.keptAs) return `Keep as ${entry.keptAs}`
  if (entry.confidence === 'check' && entry.target === null) return 'Choose a field'
  return 'Don’t import'
}

/**
 * The rows as they should look right after the user changed some, before the next preview answers:
 * a chosen field is `exact` (what the API reports for it), a column set to no field is `skipped`
 * and keeps its `keptAs`. Rows without a choice are returned as they are.
 */
export function withOverrides(
  mapping: ContactImportMappingEntry[],
  overrides: ContactImportMapping,
): ContactImportMappingEntry[] {
  return mapping.map((entry) => {
    if (!Object.prototype.hasOwnProperty.call(overrides, entry.header)) return entry
    const target = overrides[entry.header] ?? null
    if (target !== null) return { header: entry.header, target, confidence: 'exact' }
    return entry.keptAs
      ? { header: entry.header, target: null, confidence: 'skipped', keptAs: entry.keptAs }
      : { header: entry.header, target: null, confidence: 'skipped' }
  })
}

/** Header -> field (null: none) for every column: what "Save this mapping" stores. */
export function effectiveMapping(mapping: ContactImportMappingEntry[]): ContactImportMapping {
  const out: ContactImportMapping = {}
  for (const entry of mapping) out[entry.header] = entry.target
  return out
}

// --- Saved mapping ---------------------------------------------------------------------------------

export const SAVED_MAPPING_PREFIX = 'nerdcrm:import-mapping:'

/** Lower case, letters and digits only, BOM removed: the API's normalizeHeader. */
export function normaliseHeader(header: string): string {
  return (header || '')
    .replace(/^﻿/, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '')
}

/** Which saved mapping a file gets: its columns, whatever their order, case or punctuation. */
export function savedMappingKey(headers: string[]): string {
  return Array.from(new Set(headers.map(normaliseHeader).filter(Boolean)))
    .sort()
    .join(',')
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

function browserStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null // reading `localStorage` itself throws when site data is blocked
  }
}

const FIELD_SET = new Set<string>(CONTACT_IMPORT_FIELDS)

/** Stores the mapping for files with these columns. False when this browser cannot store it. */
export function saveMapping(
  headers: string[],
  mapping: ContactImportMapping,
  storage: StorageLike | null = browserStorage(),
): boolean {
  if (!storage) return false
  const byName: Record<string, ContactImportField | null> = {}
  for (const header of headers) {
    const key = normaliseHeader(header)
    if (!key || key in byName || !Object.prototype.hasOwnProperty.call(mapping, header)) continue
    byName[key] = mapping[header] ?? null
  }
  try {
    storage.setItem(`${SAVED_MAPPING_PREFIX}${savedMappingKey(headers)}`, JSON.stringify({ version: 1, mapping: byName }))
    return true
  } catch {
    return false
  }
}

/**
 * The mapping saved for a file with these columns, keyed by this file's headers; null when there is
 * none (or it cannot be read). Fields the API does not know are dropped.
 */
export function loadSavedMapping(
  headers: string[],
  storage: StorageLike | null = browserStorage(),
): ContactImportMapping | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(`${SAVED_MAPPING_PREFIX}${savedMappingKey(headers)}`)
    if (!raw) return null
    const saved = JSON.parse(raw) as { mapping?: unknown }
    const byName = saved && typeof saved === 'object' && !Array.isArray(saved) ? saved.mapping : null
    if (!byName || typeof byName !== 'object' || Array.isArray(byName)) return null

    const out: ContactImportMapping = {}
    for (const header of headers) {
      const key = normaliseHeader(header)
      if (!key || !Object.prototype.hasOwnProperty.call(byName, key)) continue
      const target = (byName as Record<string, unknown>)[key]
      if (target === null) out[header] = null
      else if (typeof target === 'string' && FIELD_SET.has(target)) out[header] = target as ContactImportField
    }
    return Object.keys(out).length > 0 ? out : null
  } catch {
    return null
  }
}

// --- Sample values ---------------------------------------------------------------------------------

/** The first non-empty value of a column in the preview's sample rows ('' when all are empty). */
export function sampleFor(header: string, rows: Record<string, string>[]): string {
  for (const row of rows) {
    const value = (row[header] ?? '').trim()
    if (value) return value
  }
  return ''
}

/** Phones, counts, dates and times: shown in Geist Mono like every other number. */
export function isNumericSample(value: string): boolean {
  return /\d/.test(value) && /^[\d\s+().:/T-]+$/.test(value.trim())
}

// --- Counts and copy -------------------------------------------------------------------------------

export interface ImportStats {
  new: number
  duplicates: number
  errors: number
}

/**
 * The dialog's duplicate choice until the user picks another: the file's values update the existing
 * contacts, which is what a re-import has always done (without clearing what the file leaves empty).
 */
export const DEFAULT_DUPLICATE_MODE: ContactImportDuplicateMode = 'update'

/** Contacts the import creates: the new rows, plus the duplicates when they are imported anyway. */
export function importCount(stats: ImportStats, mode: ContactImportDuplicateMode): number {
  return stats.new + (mode === 'create' ? stats.duplicates : 0)
}

/** Whether the import would do anything: add contacts, or update the duplicates (either update mode). */
export function hasImportWork(stats: ImportStats, mode: ContactImportDuplicateMode): boolean {
  if (importCount(stats, mode) > 0) return true
  return (mode === 'update' || mode === 'update_empty') && stats.duplicates > 0
}

/** What happens to the duplicates, for the Review step. */
export function duplicateOutcome(mode: ContactImportDuplicateMode, count: number): string {
  if (count === 0) return 'No duplicates found.'
  const n = `${count} duplicate${count === 1 ? '' : 's'}`
  if (mode === 'update') {
    return `${n} ${count === 1 ? 'updates the existing contact' : 'update the existing contacts'} with the file’s values. Empty cells change nothing.`
  }
  if (mode === 'update_empty') {
    return `${n} ${count === 1 ? 'fills' : 'fill'} in the blank fields of the existing contact. Nothing already filled in changes.`
  }
  if (mode === 'create') return `${n} ${count === 1 ? 'is' : 'are'} imported as new contacts.`
  return `${n} ${count === 1 ? 'is' : 'are'} skipped. You get the list when the import finishes.`
}

/** A row error as a few words for the list under "What will happen". */
export function shortRowError(message: string): string {
  if (/^Name is required/i.test(message)) return 'No name'
  return message
}

/** The link under "What will happen": "See the 3 rows (no name)". */
export function errorRowsLink(total: number, rows: { row: number; message: string }[]): string {
  const reasons = new Set(rows.map((r) => shortRowError(r.message)))
  const why = reasons.size === 1 ? ` (${[...reasons][0]!.toLowerCase()})` : ''
  if (rows.length < total) return `See the first ${rows.length} rows${why}`
  return total === 1 ? `See the row${why}` : `See the ${total} rows${why}`
}

// --- Files -----------------------------------------------------------------------------------------

export const MAX_CSV_BYTES = 10 * 1024 * 1024

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/** Why a picked or dropped file cannot be imported, or null. The server's own limits: .csv, 10MB. */
export function checkCsvFile(file: { name: string; size: number; type?: string }): string | null {
  const isCsv = /\.csv$/i.test(file.name) || file.type === 'text/csv'
  if (!isCsv) return 'Only .csv files can be imported. Save the sheet as CSV and try again.'
  if (file.size === 0) return 'This file is empty.'
  if (file.size > MAX_CSV_BYTES) return `This file is ${formatFileSize(file.size)}. Files up to 10MB can be imported.`
  return null
}

export interface DuplicateRow {
  row: number
  firstName: string
  lastName: string
  email: string
  phone: string
  company: string
  matchedBy: 'email' | 'phone'
  existingContactId: string
}

/** The "list of duplicates" CSV both contact importers hand out (the Facebook importer's format). */
export function duplicatesCsv(duplicates: DuplicateRow[]): string {
  const headers = ['Row', 'First Name', 'Last Name', 'Email', 'Phone', 'Company', 'Matched By', 'Existing Contact ID']
  const escape = (value: string) =>
    value.includes(',') || value.includes('"') || value.includes('\n') ? `"${value.replace(/"/g, '""')}"` : value
  const rows = duplicates.map((dup) =>
    [
      String(dup.row),
      dup.firstName,
      dup.lastName,
      dup.email,
      dup.phone,
      dup.company,
      dup.matchedBy,
      dup.existingContactId,
    ].map((value) => escape(value ?? '')),
  )
  return [headers.join(','), ...rows.map((row) => row.join(','))].join('\n')
}

/** Saves the duplicates list as duplicates-YYYY-MM-DD.csv (browser only). */
export function downloadDuplicatesCsv(duplicates: DuplicateRow[]): void {
  const blob = new Blob([duplicatesCsv(duplicates)], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `duplicates-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// --- Request failures ------------------------------------------------------------------------------

/**
 * Said when the page lost the import's answer. The import runs inside the POST, so the server can
 * still be importing after the page stopped waiting: never "try again" here, a second run imports the
 * same rows twice (deals are never deduplicated; contacts are in "import anyway" mode).
 */
export const IMPORT_TIMEOUT_MESSAGE =
  'The page stopped waiting before the server answered. The import may still be running: check the list in a few minutes before you import this file again.'

/** A preview writes nothing; when the server is slow or out of reach that is what to say, not "bad file". */
export const PREVIEW_UNREACHABLE_MESSAGE =
  'The server did not answer in time while reading this file. Try again in a moment, or split the file into smaller ones.'

interface RequestError {
  code?: unknown
  name?: unknown
  isAxiosError?: unknown
  isDemoMode?: unknown
  request?: unknown
  response?: { status?: number }
}

/** A request the dialog aborted itself (a newer preview replaced it): not an error to show. */
export function isCanceledRequest(err: unknown): boolean {
  const e = err as RequestError | null
  return !!e && (e.code === 'ERR_CANCELED' || e.name === 'CanceledError' || e.name === 'AbortError')
}

const GATEWAY_STATUSES = new Set([502, 503, 504])

/**
 * Whether the server may still be running the request although the page got no usable answer: the
 * page's own timeout, a proxy that gave up (nginx 502/503/504, usually with an HTML body) or a
 * connection that dropped (an axios error without a response). Not for an answer from the API, a
 * request the page cancelled, the demo org's block, or an error thrown before anything was sent.
 */
export function importMayStillFinish(err: unknown): boolean {
  const e = err as RequestError | null
  if (!e || typeof e !== 'object' || e.isDemoMode === true || isCanceledRequest(e)) return false
  if (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT') return true
  if (e.response) return GATEWAY_STATUSES.has(Number(e.response.status))
  return e.isAxiosError === true || e.code === 'ERR_NETWORK' || e.request !== undefined
}

/** The sentence for a failed import (see IMPORT_TIMEOUT_MESSAGE), else the API's message or `fallback`. */
export function importFailureMessage(err: unknown, fallback: string): string {
  if (importMayStillFinish(err)) return IMPORT_TIMEOUT_MESSAGE
  return apiErrorMessage(err, fallback)
}

/** The sentence for a failed preview: a slow or unreachable server, else the API's message or `fallback`. */
export function previewFailureMessage(err: unknown, fallback: string): string {
  if (importMayStillFinish(err)) return PREVIEW_UNREACHABLE_MESSAGE
  return apiErrorMessage(err, fallback)
}

/**
 * The job to show (and hand to the list's `imported`) when the page lost the import's answer: still
 * running as far as anyone knows, so no result and nothing counted.
 */
export function unconfirmedJob(
  entity: 'contacts' | 'companies' | 'deals',
  fileName: string,
  createdAt: string = new Date().toISOString(),
): { id: string; entity: 'contacts' | 'companies' | 'deals'; status: 'processing'; fileName: string; createdAt: string } {
  return { id: '', entity, status: 'processing', fileName, createdAt }
}

// --- Owner -----------------------------------------------------------------------------------------

/**
 * The Owner select for admins: "Me" (no ownerId sent: the importer owns what they import) and the
 * org's other ACTIVE members by name. The users list carries the membership status of the active
 * org; the API refuses anyone who is not an active member.
 */
export function ownerChoices(
  users: { _id: string; name?: string; email?: string; status?: string }[],
  me: { _id?: string; name?: string; email?: string } | null,
): { value: string; label: string }[] {
  const myName = me?.name || me?.email
  const others = users
    .filter((user) => user.status === 'active' && user._id !== me?._id)
    .map((user) => ({ value: user._id, label: user.name || user.email || 'Unnamed member' }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
  return [{ value: '', label: myName ? `Me (${myName})` : 'Me' }, ...others]
}
